import React, { useState } from "react";
import { Lock, Check } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageBits";
import LeadForm from "@/components/LeadForm";
import { Badge } from "@/components/ui/badge";

const TIERS = [
  { name: "Hotel Grade Tea Powder", note: "Best value for high volume", sizes: ["25 kg", "50 kg", "100 kg+"], moq: "25 kg" },
  { name: "Premium Tea Powder", note: "Signature everyday blend", sizes: ["10 kg", "25 kg", "50 kg+"], moq: "10 kg" },
  { name: "Elaichi / Masala Tea Powder", note: "Flavoured specialty", sizes: ["10 kg", "25 kg", "50 kg+"], moq: "10 kg" },
  { name: "Chocolate Tea Powder", note: "Cafe specialty", sizes: ["10 kg", "25 kg"], moq: "10 kg" },
];

export default function Wholesale() {
  useSEO({ title: "Wholesale Pricing", description: "Unlock Leaf Alchemy's wholesale tea powder pricing. Submit your business details to view bulk rates and MOQ options." });
  const [unlocked, setUnlocked] = useState(false);

  return (
    <div data-testid="wholesale-page">
      <PageHero eyebrow="For Businesses" title="Wholesale Pricing"
        subtitle="Transparent bulk rates for verified businesses. Share your details to unlock pricing and MOQ options." />

      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {!unlocked ? (
            <Reveal>
              <div className="max-w-lg mx-auto bg-cream border border-gold/30 rounded-2xl p-8 shadow-[0_16px_48px_rgba(15,81,50,0.08)] text-center" data-testid="wholesale-gate">
                <div className="w-14 h-14 rounded-full bg-forest/10 grid place-items-center text-forest mx-auto mb-4"><Lock size={24} /></div>
                <h2 className="font-display text-3xl text-forest">Unlock Wholesale Rates</h2>
                <p className="text-muted-foreground mt-2 mb-6 text-sm">Enter your business details below to instantly view our wholesale pricing structure.</p>
                <div className="text-left">
                  <LeadForm type="quote" fields={["name", "phone", "email", "company", "business_type"]} submitLabel="Unlock Pricing" onSuccess={() => setUnlocked(true)} />
                </div>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <div data-testid="wholesale-unlocked">
                <div className="text-center mb-10">
                  <Badge className="bg-olive text-white mb-3">Pricing Unlocked</Badge>
                  <h2 className="font-display text-3xl text-forest">Indicative Wholesale Structure</h2>
                  <p className="text-muted-foreground text-sm mt-2">Final pricing depends on volume, blend and location. Our team will confirm a formal quote.</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  {TIERS.map((t) => (
                    <div key={t.name} className="bg-cream border border-forest/10 rounded-2xl p-6 hover:border-gold/40 transition-colors">
                      <h3 className="font-display text-xl text-forest">{t.name}</h3>
                      <p className="text-sm text-olive mb-4">{t.note}</p>
                      <div className="space-y-2">
                        {t.sizes.map((s) => (
                          <div key={s} className="flex items-center gap-2 text-sm text-forest/80"><Check size={15} className="text-gold" /> {s} packs available</div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">MOQ: <b className="text-forest">{t.moq}</b></p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </div>
  );
}
